'use client';

import { c as _c } from "react/compiler-runtime";
import { motion } from 'framer-motion';
import { AlertCircle, ChevronRight, FileSearch } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Markdown } from '@/components/chat/markdown';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
type ExaCodeContextBlockProps = {
  query?: string;
  output?: unknown;
  errorText?: string;
};
const MAX_OUTPUT_CHARS = 12000;
const truncateText = (value: string, maxLength: number) => {
  if (value.length <= maxLength) {
    return {
      text: value,
      truncated: false
    };
  }
  return {
    text: `${value.slice(0, maxLength).trimEnd()}...`,
    truncated: true
  };
};
const normalizeOutput = (output: unknown) => {
  if (typeof output === 'string') {
    return output;
  }
  if (output && typeof output === 'object') {
    const outputRecord = output as Record<string, unknown>;
    const textCandidate = outputRecord.text ?? outputRecord.output ?? outputRecord.content ?? outputRecord.result;
    if (typeof textCandidate === 'string') {
      return textCandidate;
    }
    return `\`\`\`json\n${JSON.stringify(output, null, 2)}\n\`\`\``;
  }
  if (output == null) {
    return '';
  }
  return String(output);
};
export function ExaCodeContextBlock(t0) {
  const $ = _c(40);
  const {
    query,
    output,
    errorText
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== output) {
    t1 = normalizeOutput(output);
    $[0] = output;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const normalizedOutput = t1;
  let t2;
  if ($[2] !== normalizedOutput) {
    t2 = truncateText(normalizedOutput, MAX_OUTPUT_CHARS);
    $[2] = normalizedOutput;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const trimmedOutput = t2;
  if (errorText) {
    let t3;
    let t4;
    let t5;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        opacity: 0,
        y: 6
      };
      t4 = {
        opacity: 1,
        y: 0
      };
      t5 = {
        duration: 0.2
      };
      $[4] = t3;
      $[5] = t4;
      $[6] = t5;
    } else {
      t3 = $[4];
      t4 = $[5];
      t5 = $[6];
    }
    let t6;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    let t7;
    if ($[8] !== errorText) {
      t7 = <motion.div initial={t3} animate={t4} transition={t5} className="mt-2 flex box-border min-w-0 w-full max-w-full items-start gap-2 overflow-hidden rounded-md border border-red-800/30 bg-red-900/10 p-2 text-xs text-red-200">{t6}<div className="text-xs">Documentation lookup failed:{errorText}</div></motion.div>;
      $[8] = errorText;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    return t7;
  }
  if (!normalizedOutput) {
    let t3;
    let t4;
    let t5;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        opacity: 0,
        y: 6
      };
      t4 = {
        opacity: 1,
        y: 0
      };
      t5 = {
        duration: 0.2
      };
      $[10] = t3;
      $[11] = t4;
      $[12] = t5;
    } else {
      t3 = $[10];
      t4 = $[11];
      t5 = $[12];
    }
    let t6;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <motion.div initial={t3} animate={t4} transition={t5} className="mt-2 flex box-border min-w-0 w-full max-w-full items-center gap-2 overflow-hidden rounded-md border border-border bg-surface p-2 text-xs text-zinc-200"><FileSearch className="h-4 w-4 shrink-0 text-zinc-400" /><span className="text-xs">No external sources returned.</span></motion.div>;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    return t6;
  }
  let t3;
  let t4;
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0,
      y: 6
    };
    t4 = {
      opacity: 1,
      y: 0
    };
    t5 = {
      duration: 0.2
    };
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
  }
  let t6;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <FileSearch className="h-4 w-4 shrink-0 text-zinc-400" />;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const t7 = query ? ` for "${query}"` : "";
  let t8;
  if ($[18] !== t7) {
    t8 = <span className="min-w-0 flex-1 wrap-anywhere">Sources{t7}</span>;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const t9 = open ? "rotate(90deg)" : "rotate(0deg)";
  let t10;
  if ($[20] !== t9) {
    t10 = {
      transform: t9
    };
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ChevronRight className="h-4 w-4" />;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10) {
    t12 = <span className="transition-transform duration-200" style={t10} aria-hidden="true">{t11}</span>;
    $[23] = t10;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== open || $[26] !== t12 || $[27] !== t8) {
    t13 = <CollapsibleTrigger asChild={true}><button type="button" className="flex min-w-0 w-full items-center gap-2 text-left text-xs font-medium text-zinc-200 hover:text-zinc-100" aria-expanded={open}>{t6}{t8}{t12}</button></CollapsibleTrigger>;
    $[25] = open;
    $[26] = t12;
    $[27] = t8;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== trimmedOutput.text) {
    t14 = <Markdown className="[&_h1]:text-xs [&_h2]:text-xs [&_h3]:text-xs [&_h4]:text-xs [&_h5]:text-xs [&_h6]:text-xs [&_li]:text-xs [&_ol]:text-xs [&_p]:text-xs [&_table]:text-xs [&_td]:text-xs [&_th]:text-xs [&_ul]:text-xs">{trimmedOutput.text}</Markdown>;
    $[29] = trimmedOutput.text;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== trimmedOutput.truncated) {
    t15 = trimmedOutput.truncated ? <p className="mt-2 text-xs text-zinc-500">Results truncated for display.</p> : null;
    $[31] = trimmedOutput.truncated;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t14 || $[34] !== t15) {
    t16 = <CollapsibleContent className="mt-2 min-w-0 max-w-full max-h-96 overflow-y-auto pr-1">{t14}{t15}</CollapsibleContent>;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== open || $[37] !== t13 || $[38] !== t16) {
    t17 = <motion.div initial={t3} animate={t4} transition={t5} className="mt-2 box-border min-w-0 w-full max-w-full overflow-hidden rounded-md border border-border bg-surface p-2 text-xs text-zinc-200"><Collapsible open={open} onOpenChange={setOpen}>{t13}{t16}</Collapsible></motion.div>;
    $[36] = open;
    $[37] = t13;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  return t17;
}